import React, { Component } from 'react';
import { Checkbox, Divider, EditableUrl, Heading } from '@wix/wix-base-ui';
import * as platformEvents from 'platformEvents';
import * as util from '#packages/util';
import * as panels from '#packages/panels';
import { button as BaseUiButton } from '#packages/baseUI';
import { translate } from '#packages/i18n';
import * as customTemplatePublishSuccessPanelMapper from './customTemplatePublishSuccessPanelMapper';
import '@wix/santa-editor-utils';
import { Text, TextButton } from '@wix/design-system';
import * as coreBi from '#packages/coreBi';
import type { BiEventDefinition } from 'types/bi';
import type { EditorAPI } from '#packages/editorAPI';
import {
  TEMPLATE_LIVE_VIEW_PREFIX,
  getCustomTemplateLearnMoreLink,
} from './customTemplatePublishSuccessPanelConstants';
import {
  BiActions,
  type CustomTemplatePublishSuccessPanelProps,
  type PublishSuccessPanelState,
} from './customTemplatePublishSuccessPanelTypes';
import constants from '#packages/constants';

const {
  connect,
  STORES: { EDITOR_API },
} = util.hoc;

class CustomTemplatePublishSuccessPanel extends Component<
  CustomTemplatePublishSuccessPanelProps,
  PublishSuccessPanelState
> {
  panelName: string;
  constructor(props: CustomTemplatePublishSuccessPanelProps) {
    super(props);
    this.panelName =
      constants.PANELS.CUSTOM_TEMPLATE.CUSTOM_TEMPLATE_PUBLISH_SUCCESS_PANEL_NAME;
    this.state = {
      dontShowAgain: false,
    };
  }

  componentWillUnmount() {
    const editorAPI: EditorAPI = this.getEditorAPI();

    editorAPI.dsActions.platform.notifyAppsOnCustomEvent(
      platformEvents.factory.sitePublishedDialogClosed({}),
    );

    editorAPI.mobile.mobileDiscoverabilityModal.show();
  }

  componentDidMount() {
    // if open panel requested - end interaction on the panel did mount
    // if open panel NOT requested - end interaction in the publishManager
    util.fedopsLogger.interactionEnded(util.fedopsLogger.INTERACTIONS.PUBLISH, {
      paramsOverrides: {
        sourceOfStart: this.props.sourceOfStart,
        sourceOfEnd: 'customTemplatePublishSuccessPanel',
      },
    });
  }

  getEditorAPI() {
    return this.props.editorAPI;
  }

  getShortcuts() {
    return {
      esc: () => this.onClose(BiActions.ClickEscapeKeyboard),
      enter: () => this.onDone(BiActions.ClickEnterKeyboard),
    };
  }

  onDone(action: BiActions) {
    if (this.state.dontShowAgain) {
      this.props.setDontShowPanelValue();
    }

    this.onClose(action);
  }

  onClose(action: BiActions) {
    this.sendBI(coreBi.events.customTemplate.post_publish_modal, {
      is_first_template_publish: false,
      action,
    });

    this.getEditorAPI().panelManager.closePanelByName(this.panelName);
  }

  viewYourSite = () => {
    this.sendViewSiteBI();
    window.open(this.getSiteUrl());
  };

  getSiteUrl(): string {
    return `${TEMPLATE_LIVE_VIEW_PREFIX}/${this.props.templateId}`;
  }

  sendViewSiteBI() {
    this.sendBI(coreBi.events.customTemplate.post_publish_modal, {
      is_first_template_publish: false,
      action: BiActions.ClickViewSite,
    });
  }

  toggleDontShowAgain() {
    const dontShowAgainValue = !this.state.dontShowAgain;
    this.sendToggleDontShowAgainBI(dontShowAgainValue);
    this.setState({ dontShowAgain: dontShowAgainValue });
  }

  sendToggleDontShowAgainBI(toggleValue: boolean) {
    const biToggleValue = toggleValue ? 1 : 0;

    this.sendBI(coreBi.events.publish.Second_Publish_dont_show_again_click, {
      toggle: biToggleValue,
    });
  }

  sendBI(
    biEvent: BiEventDefinition,
    biFields: Record<string, string | number | boolean>,
  ) {
    this.getEditorAPI().bi.event(biEvent, biFields);
  }

  onLearnMoreClicked = () => {
    this.sendBI(coreBi.events.customTemplate.post_publish_modal, {
      is_first_template_publish: false,
      action: BiActions.ClickLearnMore,
    });

    window.open(getCustomTemplateLearnMoreLink(), '_blank');
  };

  renderHeader() {
    return (
      <div className="publish-success-header">
        <div className="title-wrapper">
          <Heading appearance="h2">
            PUBLISH_Templates_Secondpublish_Congratulations_Modal_Title
          </Heading>
        </div>
      </div>
    );
  }

  renderBody() {
    return (
      <div className="publish-success-body">
        <div className="address-bar-wrapper">
          <div className="editable-url-wrapper">
            <EditableUrl domain={''} value={this.getSiteUrl()} isReadOnly />
          </div>
          <BaseUiButton
            className="btn-confirm-secondary"
            label="PUBLISH_Templates_Secondpublish_Congratulations_Modal_Button_View"
            onClick={this.viewYourSite}
          />
        </div>
        <div className="divider-wrapper">
          <Divider direction="horizontal" long />
        </div>
        <div className="text-wrapper">
          <Text size="medium">
            {translate(
              'PUBLISH_Templates_Secondpublish_Congratulations_Modal_Body',
            )}{' '}
            <TextButton onClick={this.onLearnMoreClicked} size="medium">
              {translate(
                'PUBLISH_Templates_Secondpublish_Congratulations_Modal_Body_Link',
              )}
            </TextButton>
          </Text>
        </div>
      </div>
    );
  }

  render() {
    return (
      <panels.frames.CustomPanelFrame
        panelName={this.panelName}
        className="custom-template-publish-success-panel"
        dataHook="custom-template-publish-success-panel"
        onCloseButtonClick={() => this.onClose(BiActions.ClickClose)}
        onOuterClick={() => this.onClose(BiActions.ClickOutside)}
        keyboardShortcuts={this.getShortcuts()}
        primaryButtonText={translate(
          'PUBLISH_Templates_Secondpublish_Congratulations_Modal_Button_Done',
        )}
        onPrimaryButtonClick={() => this.onDone(BiActions.ClickDone)}
        sideActions={
          <Checkbox
            key="dontShowAgain"
            label="PUBLISH_Templates_Secondpublish_Congratulations_Modal_Do_Not_Show_Again_Label"
            value={this.state.dontShowAgain}
            onChange={() => this.toggleDontShowAgain()}
            labelAfterSymbol={true}
            className="dont-show-again"
          />
        }
      >
        {this.renderHeader()}
        {this.renderBody()}
      </panels.frames.CustomPanelFrame>
    );
  }
}

const connectedCustomTemplatePublishSuccessPanel = connect(
  EDITOR_API,
  customTemplatePublishSuccessPanelMapper.mapStateToProps,
  customTemplatePublishSuccessPanelMapper.mapDispatchToProps,
)(CustomTemplatePublishSuccessPanel);

connectedCustomTemplatePublishSuccessPanel.pure =
  CustomTemplatePublishSuccessPanel;

export default connectedCustomTemplatePublishSuccessPanel;
